import debounce from 'lodash.debounce';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { Config } from 'config';
import { useAppSelector } from 'store';
import { getToken } from 'utils/storage';
import { useTwilio } from 'context/comm/twilio';
import { useLazyListQuery } from 'store/api/action-queue';
import { useAblyChannel, useAblyEventChannel } from 'hooks';
import { selectAuthUser, selectAuthDataForChat } from 'store/slices/auth';

declare global {
  interface Window {
    chrome?: {
      action?: {
        setBadgeText: (details: { text: string }) => void;
        setBadgeBackgroundColor: (details: { color: string }) => void;
      };
      notifications?: {
        create: (options: {
          type: string;
          title: string;
          iconUrl: string;
          message: string;
        }) => void;
      };
      storage?: {
        local?: {
          set: (items: Record<string, any>, callback?: () => void) => void;
          get: (
            keys: string | string[] | Record<string, any> | null,
            callback?: (items: Record<string, any>) => void
          ) => void;
        };
      };
      runtime?: {
        id?: string;
        lastError?: { message: string };
        getURL: (path: string) => string;
        sendMessage: (
          message: any,
          responseCallback?: (response: any) => void
        ) => void;
        connect: (connectInfo?: { name?: string }) => {
          name: string;
          disconnect: () => void;
          postMessage: (message: any) => void;
          onDisconnect?: {
            addListener: (callback: () => void) => void;
          };
        };
      };
    };
  }
}

/**
 * Hook to handle Chrome extension queue notifications
 * Uses Ably for real-time queue updates (same as CommContext)
 */
export const useChromeExtensionQueue = () => {
  const isChromeExtension = process.env.REACT_APP_CHROME_EXTENSION === 'true';
  const isChromeExtensionAvailable =
    typeof window !== 'undefined' &&
    window.chrome?.runtime?.id &&
    window.chrome?.action;

  const [isExtensionVisible, setIsExtensionVisible] = useState(
    typeof document !== 'undefined'
      ? document.visibilityState !== 'hidden'
      : false
  );

  // Get queue data using the same API as CommContext
  const [trigger, { data: queueData }] = useLazyListQuery();
  const queueCount = queueData?.length || 0;
  const previousCountRef = useRef<number>(0);

  // Get user name for Ably connection
  const { name } = useAppSelector(selectAuthDataForChat);
  const user = useAppSelector(selectAuthUser);

  // Get Twilio support status (similar to CommContext)
  const { isTwilioSupported } = useTwilio(user);

  // Get Ably channel (same as CommContext uses)
  // useAblyChannel will return undefined if CommContext isn't ready - that's safe
  const ablyChannel = useAblyChannel('comm');

  // Track visibility changes
  useEffect(() => {
    if (!isChromeExtension) {
      return;
    }

    const handleVisibilityChange = () => {
      setIsExtensionVisible(document.visibilityState !== 'hidden');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isChromeExtension]);

  // Send Twilio support status to background.js
  useEffect(() => {
    if (!isChromeExtension || !window.chrome?.runtime) {
      return;
    }

    if (isTwilioSupported) {
      try {
        window.chrome.runtime.sendMessage(
          {
            type: 'TWILIO_SUPPORTED',
          },
          () => {
            // Check for runtime errors
            if (window.chrome?.runtime?.lastError) {
              // Silently ignore "Receiving end does not exist" errors
              // This happens when the service worker is not active
              const errorMsg = window.chrome.runtime.lastError.message;
              if (
                !errorMsg.includes('Receiving end does not exist') &&
                !errorMsg.includes('Could not establish connection')
              ) {
                console.error(
                  '[Chrome Extension] Error sending Twilio status:',
                  errorMsg
                );
              }
            }
          }
        );
      } catch (error) {
        // Silently ignore connection errors
      }
    }
  }, [isChromeExtension, isTwilioSupported]);

  // Save Ably credentials to background.js (similar to how Twilio gets initialized)
  useEffect(() => {
    if (!isChromeExtension || !window.chrome?.runtime || !name) {
      return;
    }

    try {
      const token = getToken();
      const apiBaseURL = Config.baseURL;
      const ablyPublicKey = process.env.REACT_APP_PUBLIC_ABLY_API_KEY || '';

      if (token && apiBaseURL) {
        // Send credentials to background.js
        try {
          window.chrome.runtime.sendMessage(
            {
              userName: name,
              authToken: token,
              apiBaseURL: apiBaseURL,
              ablyPublicKey: ablyPublicKey,
              type: 'SAVE_ABLY_CREDENTIALS',
              ablyChannel: Config.ablyChannel,
            },
            () => {
              // Check for runtime errors
              if (window.chrome?.runtime?.lastError) {
                // Silently ignore "Receiving end does not exist" errors
                // This happens when the service worker is not active
                const errorMsg = window.chrome.runtime.lastError.message;
                if (
                  !errorMsg.includes('Receiving end does not exist') &&
                  !errorMsg.includes('Could not establish connection')
                ) {
                  console.error(
                    '[Chrome Extension] Error saving credentials:',
                    errorMsg
                  );
                }
              } else {
                console.log(
                  '[Chrome Extension] Credentials sent to background.js'
                );
              }
            }
          );
        } catch (error) {
          // Silently ignore connection errors
        }
      }
    } catch (error) {
      console.error('[Chrome Extension] Error saving credentials:', error);
    }
  }, [isChromeExtension, name]);

  // Refetch queue when Ably event fires (real-time updates)
  const refetch = useCallback(() => {
    if (isChromeExtension) {
      try {
        trigger();
      } catch (error) {
        console.error(
          '[Chrome Extension] Error triggering queue fetch:',
          error
        );
      }
    }
  }, [isChromeExtension, trigger]);

  // Listen to queue_updated events from Ably (same as CommContext)
  // Only subscribe if channel is available (useAblyEventChannel handles undefined safely)
  const handleQueueUpdated = useCallback(() => {
    // When queue_updated event fires, fetch queue
    if (isChromeExtension) {
      refetch();
    }
  }, [isChromeExtension, refetch]);

  const debouncedHandleQueueUpdated = useMemo(
    () => debounce(handleQueueUpdated, 1000),
    [handleQueueUpdated]
  );

  useAblyEventChannel(
    isChromeExtension && ablyChannel ? 'queue_updated' : undefined,
    debouncedHandleQueueUpdated,
    ablyChannel
  );

  // Initial fetch
  useEffect(() => {
    if (isChromeExtension) {
      trigger();
    }
  }, [isChromeExtension, trigger]);

  // Update badge and send notifications when queue count changes
  useEffect(() => {
    if (
      !isChromeExtension ||
      !isChromeExtensionAvailable ||
      !window.chrome?.action ||
      !window.chrome?.runtime
    ) {
      return;
    }

    const currentCount = queueCount;

    // Update badge
    if (currentCount > 0) {
      window.chrome.action.setBadgeText({ text: String(currentCount) });
      window.chrome.action.setBadgeBackgroundColor({ color: '#FF0000' });
    } else {
      window.chrome.action.setBadgeText({ text: '' });
    }

    previousCountRef.current = currentCount;
  }, [
    isChromeExtension,
    isChromeExtensionAvailable,
    queueCount,
    isExtensionVisible,
  ]);

  // Notify background script about side panel state
  useEffect(() => {
    if (
      !isChromeExtension ||
      !isChromeExtensionAvailable ||
      !window.chrome?.runtime
    ) {
      return;
    }

    const port = window.chrome.runtime.connect({ name: 'sidePanel' });

    return () => {
      try {
        port.disconnect();
      } catch (error) {
        // Ignore disconnect errors
      }
    };
  }, [isChromeExtension, isChromeExtensionAvailable, isExtensionVisible]);

  // Cleanup badge on unmount
  useEffect(() => {
    return () => {
      if (
        isChromeExtension &&
        isChromeExtensionAvailable &&
        window.chrome?.action
      ) {
        window.chrome.action.setBadgeText({ text: '' });
      }
    };
  }, [isChromeExtension, isChromeExtensionAvailable]);
};
